import { createContext, RefObject, useContext, useRef, useState } from 'react';
import ReactCrop, { convertToPixelCrop, Crop } from 'react-image-crop';

import styled from 'styled-components';

import { Button } from '@components/button';
import { FileDropzone } from '@components/file-dropzone';
import { HorizontalTabItem, HorizontalTabs } from '@components/horizontal-tabs';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconImage } from '@components/icons/image';
import { IconReplace } from '@components/icons/replace';
import { IconSearch } from '@components/icons/search';
import { IconUpload } from '@components/icons/upload';
import { Modal, useModal } from '@components/modal';
import { hideModal, MODAL_FOOTER_CLASSNAME } from '@components/modal/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { base64ToFile, centerAspectCrop, getCroppedImage } from './helpers';
import { Unsplash } from './unsplash';

import 'react-image-crop/dist/ReactCrop.css';

export type AddImageProps = {
  url?: string;
  onChange: (photoFile: File) => void;
  handleUpload?(file: File, setProgress: (progress: number) => void): void;
};

type EditImageContextValue = {
  imgRef: RefObject<HTMLImageElement>;
  setCompletedCrop: (crop: Crop) => void;
  isEditing?: boolean;
};

const EditImageContext = createContext<EditImageContextValue>({} as EditImageContextValue);

export const ImageNode = ({ url }: Pick<AddImageProps, 'url'>) => {
  return url ? <EdittableImage src={url} /> : <Fallback />;
};

export const AddImage = ({ url, ...rest }: AddImageProps) => {
  const { t } = useScopedTranslate(locale);
  const showModal = useModal(AddImageModal, { variant: 'LARGE' });
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleCrop = async () => {
    if (imgRef.current && completedCrop) {
      const cropped = getCroppedImage(imgRef.current, completedCrop);
      if (cropped) {
        const file = base64ToFile(cropped, 'cropped.jpg');
        setIsSaving(true);
        await rest.onChange(file);
        setIsSaving(false);
      }
    }
  };

  return (
    <EditImageContext.Provider value={{ imgRef, setCompletedCrop, isEditing }}>
      <ImageNode url={url} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Stack orientation="horizontal" space={2}>
          {url && !isEditing && (
            <Button
              variant="secondary"
              onClick={() => setIsEditing(true)}
              text={t('editImage')}
              iconLeft={<IconEdit />}
            />
          )}
          {isEditing ? (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setCompletedCrop(null);
                  setIsEditing(false);
                }}
                isDisabled={isSaving}
                text={t('core.cancel')}
              />
              <Button
                variant="primary-blue"
                onClick={async () => {
                  await handleCrop();
                  setCompletedCrop(null);
                  setIsEditing(false);
                }}
                isDisabled={!completedCrop || isSaving}
                text={t('core.save')}
              />
            </>
          ) : (
            <Button
              variant="secondary"
              onClick={() => showModal(rest)}
              text={url ? t('replaceImage') : t('addImage')}
              iconLeft={url ? <IconReplace /> : <IconAddPlus />}
            />
          )}
        </Stack>
      </div>
    </EditImageContext.Provider>
  );
};

type AddImageModalProps = Omit<AddImageProps, 'url'>;

export const AddImageModal = ({ onChange, handleUpload }: AddImageModalProps) => {
  const { t } = useScopedTranslate(locale);
  const tabItems = [
    {
      label: t('upload'),
      icon: IconUpload,
      content: (
        <TabContainer>
          <FileDropzone
            onChange={handleUpload ? undefined : onChange}
            handleUpload={handleUpload}
            onFinish={hideModal}
            fileTypes={['jpg', 'png', 'gif']}
          />
        </TabContainer>
      ),
    },
    {
      label: t('unsplash'),
      icon: IconSearch,
      content: (
        <TabContainer>
          <Unsplash onSelect={onChange} />
        </TabContainer>
      ),
    },
  ].filter(Boolean) as Array<HorizontalTabItem>;

  return (
    <ImageModal>
      <Modal.Content>
        <HorizontalTabs items={tabItems} />
      </Modal.Content>
    </ImageModal>
  );
};

const ImageModal = styled(Modal)({
  'div[role="tablist"]': {
    paddingLeft: pxToRem(0),
    marginBottom: pxToRem(24),
  },
  [`.${MODAL_FOOTER_CLASSNAME}`]: {
    padding: pxToRem(0),
  },
});

export const Image = styled.img<{ $width: number }>((p) => ({
  maxWidth: '100%',
  overflow: 'hidden',
  transition: 'width 0.2s ease-out',
  width: `${p.$width}%`,
}));

type EdittableImageProps = {
  src: string;
};

const EdittableImage = ({ src }: EdittableImageProps) => {
  const [crop, setCrop] = useState<Crop>();
  const { isEditing, imgRef, setCompletedCrop } = useContext(EditImageContext);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const centerCrop = centerAspectCrop(width, height);
    setCrop(centerCrop);
    setCompletedCrop(convertToPixelCrop(centerCrop, width, height));
  };

  if (isEditing) {
    return (
      <ReactCrop
        crop={crop}
        onChange={(_, percentCrop) => setCrop(percentCrop)}
        onComplete={(c) => setCompletedCrop(c)}
      >
        <Image src={src} $width={100} ref={imgRef} onLoad={onImageLoad} crossOrigin="anonymous" />
      </ReactCrop>
    );
  }
  return <Image src={src} $width={100} />;
};

const TabContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: pxToRem(536),
  '>*': {
    flex: 1,
  },
});

const Fallback = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <FallbackContainer>
      <IconImage size={40} color="purple50" />
      <Text font="regular14" color="purple50" text={t('noImage')} />
    </FallbackContainer>
  );
};

const FallbackContainer = styled.div({
  borderRadius: pxToRem(8),
  background: theme.color.uiTrans5,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: pxToRem(16),
});

const locale = {
  en: {
    title: 'Image',
    noImage: 'No image added',
    addImage: 'Add image',
    editImage: 'Crop image',
    replaceImage: 'Replace image',
    upload: 'Upload',
    unsplash: 'Unsplash',
  },
};
